/**
 * APP版本创建/编辑表单页面
 */
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LayoutWithSidebar } from '@/components/LayoutWithSidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, X } from 'lucide-react';
import { toast } from 'sonner';
import type { CreateVersionRequest } from '@xiaozhi/shared-types';
import { UpdateType, PlatformType, VersionStatus } from '@xiaozhi/shared-types';
import { createVersion, getVersionById, updateVersion } from '@/api/appVersion';

export default function AppVersionFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [formData, setFormData] = useState<CreateVersionRequest>({
    version_name: '',
    version_code: 0,
    update_type: UpdateType.OPTIONAL,
    force_update: false,
    download_url: '',
    file_size: 0,
    file_md5: '',
    release_notes: [],
    min_support_version: '',
    platform: PlatformType.ANDROID,
    channel: 'official',
    gray_enabled: false,
    gray_percentage: 0,
    status: VersionStatus.DRAFT,
  });

  const [releaseNotesText, setReleaseNotesText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // 如果是编辑模式，加载现有数据
  useEffect(() => {
    if (isEdit && id) {
      loadVersion(Number.parseInt(id));
    }
  }, [isEdit, id]);

  const loadVersion = async (versionId: number) => {
    try {
      setLoading(true);
      const version = await getVersionById(versionId);
      
      setFormData({
        version_name: version.version_name,
        version_code: version.version_code,
        update_type: version.update_type as UpdateType,
        force_update: !!version.force_update,
        download_url: version.download_url,
        file_size: version.file_size,
        file_md5: version.file_md5,
        release_notes: typeof version.release_notes === 'string' 
          ? JSON.parse(version.release_notes) 
          : version.release_notes,
        min_support_version: version.min_support_version,
        platform: version.platform as PlatformType,
        channel: version.channel,
        gray_enabled: !!version.gray_enabled,
        gray_percentage: version.gray_percentage,
        status: version.status,
      });

      // 设置更新说明文本
      const notes = typeof version.release_notes === 'string' 
        ? JSON.parse(version.release_notes) 
        : version.release_notes;
      setReleaseNotesText(notes.join('\n'));
    } catch (error: any) {
      toast.error('加载版本信息失败', {
        description: error.message,
      });
      navigate('/app-version');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setSelectedFile(file);
    setFormData({
      ...formData,
      file_size: file.size,
    });

    // 🔥 自动解析APK/IPA文件
    if (file.name.endsWith('.apk') || file.name.endsWith('.ipa')) {
      try {
        setParsing(true);
        
        const parseFormData = new FormData();
        parseFormData.append('apk', file);
        
        const response = await fetch('/api/app-version/parse', {
          method: 'POST',
          body: parseFormData,
        });
        
        const result = await response.json();
        
        if (result.code === 0 && result.data) {
          // 自动填充表单
          setFormData(prev => ({
            ...prev,
            version_name: result.data.versionName || prev.version_name,
            version_code: result.data.versionCode || prev.version_code,
            file_size: file.size,
          }));
          
          toast.success('✅ 已自动识别版本信息', {
            description: `版本: ${result.data.versionName} (${result.data.versionCode})`,
          });
        } else {
          toast.warning('⚠️ 无法解析文件，请手动填写版本信息', {
            description: result.message,
          });
        }
      } catch (error: any) {
        console.error('解析文件失败:', error);
        toast.warning('⚠️ 无法解析文件，请手动填写版本信息');
      } finally {
        setParsing(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 验证必填项
    if (!formData.version_name.trim()) {
      toast.error('请输入版本名称');
      return;
    }
    if (formData.version_code <= 0) {
      toast.error('请输入有效的版本号');
      return;
    }
    if (!formData.min_support_version.trim()) {
      toast.error('请输入最低支持版本');
      return;
    }

    // 如果是新建版本，必须上传文件
    if (!isEdit && !selectedFile) {
      toast.error('请选择要上传的APK/IPA文件');
      return;
    }

    try {
      setLoading(true);

      // 处理更新说明
      const releaseNotes = releaseNotesText
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      const submitData = {
        ...formData,
        release_notes: releaseNotes,
      };

      if (isEdit && id) {
        // 编辑模式
        const formDataToSend = new FormData();
        Object.entries(submitData).forEach(([key, value]) => {
          if (key === 'release_notes') {
            formDataToSend.append(key, JSON.stringify(value));
          } else if (typeof value === 'boolean') {
            formDataToSend.append(key, value ? '1' : '0');
          } else {
            formDataToSend.append(key, value.toString());
          }
        });

        // 如果有新文件，添加到表单
        if (selectedFile) {
          formDataToSend.append('apk', selectedFile);
        }

        await updateVersion(Number.parseInt(id), formDataToSend);
        toast.success('版本更新成功');
      } else {
        // 创建模式
        const formDataToSend = new FormData();
        Object.entries(submitData).forEach(([key, value]) => {
          if (key === 'release_notes') {
            formDataToSend.append(key, JSON.stringify(value));
          } else if (typeof value === 'boolean') {
            formDataToSend.append(key, value ? '1' : '0');
          } else {
            formDataToSend.append(key, value.toString());
          }
        });

        if (selectedFile) {
          formDataToSend.append('apk', selectedFile);
        }

        await createVersion(formDataToSend);
        toast.success('版本创建成功');
      }

      navigate('/app-version');
    } catch (error: any) {
      toast.error(isEdit ? '更新版本失败' : '创建版本失败', {
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <LayoutWithSidebar title={isEdit ? '编辑版本' : '发布新版本'}>
      <div className="flex-1 overflow-auto">
          <div className="container mx-auto py-8 px-4 max-w-4xl">
            <div className="mb-6">
              <Button variant="ghost" onClick={() => navigate('/app-version')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                返回列表
              </Button>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>{isEdit ? '编辑版本' : '发布新版本'}</CardTitle>
                <CardDescription>
                  {isEdit ? '更新现有版本信息' : '创建新的应用版本'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* 基本信息 */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">基本信息</h3>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="version_name">
                          版本名称 <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="version_name"
                          placeholder="例如: 1.0.0"
                          value={formData.version_name}
                          onChange={(e) =>
                            setFormData({ ...formData, version_name: e.target.value })
                          }
                          required
                        />
                        <p className="text-xs text-gray-500">
                          💡 上传APK文件后会自动识别并填充
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="version_code">
                          版本号 <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="version_code"
                          type="number"
                          placeholder="例如: 10000"
                          value={formData.version_code || ''}
                          onChange={(e) =>
                            setFormData({ ...formData, version_code: Number.parseInt(e.target.value) || 0 })
                          }
                          required
                        />
                        <p className="text-xs text-gray-500">
                          💡 上传APK文件后会自动识别并填充
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="platform">平台</Label>
                        <Select
                          value={formData.platform}
                          onValueChange={(value) =>
                            setFormData({ ...formData, platform: value as PlatformType })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={PlatformType.ANDROID}>Android</SelectItem>
                            <SelectItem value={PlatformType.IOS}>iOS</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="channel">渠道</Label>
                        <Input
                          id="channel"
                          placeholder="例如: official, beta"
                          value={formData.channel}
                          onChange={(e) =>
                            setFormData({ ...formData, channel: e.target.value })
                          }
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="min_support_version">
                        最低支持版本 <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="min_support_version"
                        placeholder="例如: 1.0.0"
                        value={formData.min_support_version}
                        onChange={(e) =>
                          setFormData({ ...formData, min_support_version: e.target.value })
                        }
                        required
                      />
                    </div>
                  </div>

                  {/* 更新配置 */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">更新配置</h3>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="update_type">更新类型</Label>
                        <Select
                          value={formData.update_type}
                          onValueChange={(value) =>
                            setFormData({ ...formData, update_type: value as UpdateType })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={UpdateType.OPTIONAL}>可选更新</SelectItem>
                            <SelectItem value={UpdateType.FORCE}>强制更新</SelectItem>
                            <SelectItem value={UpdateType.SILENT}>静默更新</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="status">状态</Label>
                        <Select
                          value={formData.status.toString()}
                          onValueChange={(value) =>
                            setFormData({ ...formData, status: Number.parseInt(value) })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={VersionStatus.DRAFT.toString()}>草稿</SelectItem>
                            <SelectItem value={VersionStatus.PUBLISHED.toString()}>已发布</SelectItem>
                            <SelectItem value={VersionStatus.DISABLED.toString()}>已禁用</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="force_update"
                        checked={formData.force_update}
                        onCheckedChange={(checked) =>
                          setFormData({ ...formData, force_update: checked })
                        }
                      />
                      <Label htmlFor="force_update">强制更新</Label>
                    </div>
                  </div>

                  {/* 灰度发布 */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">灰度发布</h3>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="gray_enabled"
                        checked={formData.gray_enabled}
                        onCheckedChange={(checked) =>
                          setFormData({ ...formData, gray_enabled: checked })
                        }
                      />
                      <Label htmlFor="gray_enabled">开启灰度</Label>
                    </div>

                    {formData.gray_enabled && (
                      <div className="space-y-2">
                        <Label htmlFor="gray_percentage">灰度比例 (%)</Label>
                        <Input
                          id="gray_percentage"
                          type="number"
                          min="0"
                          max="100"
                          placeholder="0-100"
                          value={formData.gray_percentage || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              gray_percentage: Number.parseInt(e.target.value) || 0,
                            })
                          }
                        />
                      </div>
                    )}
                  </div>

                  {/* 文件上传 */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">
                      安装包 {!isEdit && <span className="text-red-500">*</span>}
                    </h3>

                    <div className="space-y-2">
                      <Label htmlFor="file">
                        {formData.platform === PlatformType.ANDROID ? 'APK文件' : 'IPA文件'}
                      </Label>
                      <div className="flex items-center gap-4">
                        <Input
                          id="file"
                          type="file"
                          accept={formData.platform === PlatformType.ANDROID ? '.apk' : '.ipa'}
                          onChange={handleFileSelect}
                          className="flex-1"
                          disabled={parsing}
                        />
                        {selectedFile && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => setSelectedFile(null)}
                            disabled={parsing}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      {parsing && (
                        <p className="text-sm text-blue-600">
                          🔄 正在解析文件，请稍候...
                        </p>
                      )}
                      {selectedFile && !parsing && (
                        <p className="text-sm text-gray-600">
                          已选择: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                        </p>
                      )}
                      {isEdit && !selectedFile && !parsing && (
                        <p className="text-sm text-gray-600">
                          当前文件: {formData.download_url} ({(formData.file_size / 1024 / 1024).toFixed(2)} MB)
                        </p>
                      )}
                    </div>
                  </div>

                  {/* 更新说明 */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">更新说明</h3>
                    <div className="space-y-2">
                      <Label htmlFor="release_notes">更新内容（每行一条）</Label>
                      <Textarea
                        id="release_notes"
                        placeholder="例如:&#10;- 新增功能A&#10;- 优化性能&#10;- 修复已知问题"
                        rows={6}
                        value={releaseNotesText}
                        onChange={(e) => setReleaseNotesText(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* 提交按钮 */}
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate('/app-version')}
                      disabled={loading}
                    >
                      取消
                    </Button>
                    <Button type="submit" disabled={loading}>
                      {loading ? '处理中...' : isEdit ? '保存更改' : '创建版本'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
      </div>
    </LayoutWithSidebar>
  );
}

